/** @jsxImportSource @emotion/react */
import {
  EmptyContent,
  Header,
  useSession,
  TruncatedString,
} from '@quickbit/backoffice-core-frontend';
import {
  Typography,
  createStyles,
  Table,
  Button,
  SearchInput,
  UtilityButton,
  useForm,
  Pagination,
  useTheme,
  Icon,
} from '@quickbit/qb-design-system';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsyncFn, useDebounce, useSessionStorage } from 'react-use';
import { QBLoader } from 'components';
import {
  MerchantSearchFilters,
  MerchantSearchResponse,
  searchMerchants,
} from '../api';

const PAGE_SIZE = 16;

export const MerchantList = () => {
  const { spacing, palette } = useTheme();
  const styles = useStyles();
  const session = useSession();
  const navigate = useNavigate();

  const [filterStore, setFilterStore] = useSessionStorage<{
    filters: MerchantSearchFilters;
    pagination: {
      page: number;
      size: number;
    };
  } | null>('qbMerchantFilterStore');

  const [page, setPage] = useState(filterStore?.pagination.page ?? 1);

  const { fields, setters } = useForm<MerchantSearchFilters>(
    {
      name: filterStore?.filters.name || null,
      merchantId: filterStore?.filters.merchantId || null,
      userId: filterStore?.filters.userId || null,
    },
    {}
  );

  const [value, setSearchStore] =
    useSessionStorage<MerchantSearchResponse | null>('qbMerchantSearchResult');

  const [{ error, loading }, getMerchants] = useAsyncFn(async () => {
    const response = await searchMerchants({
      filters: fields,
      pagination: { page, size: PAGE_SIZE },
    });
    setSearchStore(response);
    setFilterStore({ filters: fields, pagination: { page, size: PAGE_SIZE } });
  }, [fields, page]);

  const headers = [
    { label: 'Name', cellKey: 'name', sort: false },
    { label: 'Merchant ID', cellKey: 'merchantId', sort: false },
    { label: 'Country', cellKey: 'countryCode', sort: false },
    { label: 'Status', cellKey: 'status', sort: false },
  ];

  const rows = useMemo(
    () =>
      value?.merchants.map((merchant) => ({
        value: {
          uuid: merchant.uuid,
          merchantId: <TruncatedString text={merchant.uuid} />,
          name: merchant.name,
          countryCode: merchant.countryCode,
          status: merchant.status,
          deletedAt: merchant.deletedAt?.toLocaleString(),
        },
        disabled: !!merchant.deletedAt,
      })) ?? [],
    [value]
  );

  const hasPermission = useMemo(() => {
    if (
      session.authenticated &&
      !session.permissions.includes('edit_merchants')
    )
      return false;
    return true;
  }, [session]);

  {
    error && (
      <>
        <Header>
          <Typography variant="subtitle">Merchants</Typography>
        </Header>
        <EmptyContent
          heading="Oops! It's empty."
          body="Something went wrong"
          action={
            <Button text="Try again" variant="primary" onClick={getMerchants} />
          }
        />
      </>
    );
  }
  useDebounce(
    () => {
      void getMerchants();
    },
    150,
    [getMerchants]
  );

  const handleResetFilters = () => {
    setPage(1);
    setters.set({ name: null, merchantId: null });
  };

  return (
    <>
      <Header>
        <div css={[styles.headerContent]}>
          <div css={styles.titleContainer}>
            <Typography variant="subtitle">Merchants</Typography>
          </div>

          <UtilityButton
            text="Add merchant"
            variant="primary"
            icon="plus"
            disabled={!hasPermission}
            onClick={() => navigate('new')}
          />
        </div>
      </Header>

      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, auto) 1fr',
          gap: spacing.xxxs,
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        <SearchInput
          placeholder="Name"
          value={fields.name ?? ''}
          onChange={(e) => setters.name?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Merchant ID"
          value={fields.merchantId ?? ''}
          onChange={(e) => setters.merchantId?.set(e.target.value)}
        />
        <SearchInput
          placeholder="User ID"
          value={fields.userId ?? ''}
          onChange={(e) => setters.userId?.set(e.target.value)}
        />
      </div>

      <div
        css={{
          marginTop: spacing.xxs,
          marginBottom: spacing.xxs,
          display: 'grid',
          gridTemplateColumns: 'repeat(99, auto) 1fr',
          alignItems: 'center',
          gap: spacing.xxxs,
          textWrap: 'nowrap',
        }}
      >
        <UtilityButton
          variant="text"
          text="Reset filters"
          onClick={handleResetFilters}
        />
        <UtilityButton
          variant="secondary"
          text="Refresh"
          onClick={getMerchants}
          disabled={loading}
        />
        {loading && (
          <Icon
            name="spinner"
            className={{ svg: { color: palette.grey.default } }}
          />
        )}
      </div>

      {loading && <QBLoader />}
      {!loading && value && (
        <>
          <div css={{ marginTop: spacing.xxs }}>
            <Table
              headers={headers}
              rows={rows}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
              onRowClick={(r: any) => navigate(r.uuid)}
            />
          </div>
          <div css={{ marginTop: spacing.xxs }}>
            <Pagination
              currentPage={page}
              onChange={setPage}
              numberOfElements={value.aggregatedData.count}
              pageSize={PAGE_SIZE}
            />
          </div>
        </>
      )}
    </>
  );
};

const useStyles = createStyles(({ palette, shape, spacing }) => ({
  headerContent: {
    display: 'grid',
    gap: spacing.xxs,
    gridTemplateColumns: '1fr auto auto',
    'svg:not(button *)': {
      color: palette.grey.x900,
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  tableWrapper: {
    border: `1px solid ${palette.grey.x100}`,
    borderRadius: shape.borderRadius,
  },
}));
